import { Button, useSnackbar } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useNavigate } from "react-router-dom";
import { type CheckoutConfigOptionGroupStepDetail } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import { useReviewStep_SaveProductOfferingMutation } from "src/routes/libraries/product-offerings/add-product-offering/components/5.ReviewStep.generated";
import {
  EditProductOfferingFragment,
  useEditProductOfferingPageQuery,
} from "src/routes/libraries/product-offerings/edit-product-offering/EditProductOfferingPage.generated";
import { ProductOfferingVersionParams } from "src/routes/routesDef";
import { createDevelopmentProductOfferingUrl, createProjectProductOfferingUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { formConfig } from "../add-product-offering/AddProductOfferingPage";
import { OptionsView } from "../add-product-offering/components/4.OptionsStep";

export function EditProductOfferingPage() {
  const { productOfferingId, rpavId } = useRouteParams<ProductOfferingVersionParams>();
  const query = useEditProductOfferingPageQuery({ variables: { id: productOfferingId, versionId: rpavId } });
  return queryResult(query, ({ productOffering, enumDetails }) => (
    <EditProductOfferingView
      productOffering={productOffering}
      checkoutConfigOptionGroupSteps={enumDetails.checkoutConfigOptionGroupStep}
    />
  ));
}

type EditProductOfferingViewProps = {
  productOffering: EditProductOfferingFragment;
  checkoutConfigOptionGroupSteps: CheckoutConfigOptionGroupStepDetail[];
};

export function EditProductOfferingView(props: EditProductOfferingViewProps) {
  const { productOffering, checkoutConfigOptionGroupSteps } = props;
  const [{ devId, projectId }] = useQueryParams({ devId: StringParam, projectId: StringParam });
  const readOnly = !(productOffering.canEdit.allowed && productOffering.canEditOptions?.allowed);
  const disabledReasons = [
    ...(productOffering.canEdit.disabledReasons?.map((r) => r.message) ?? []),
    ...(productOffering.canEditOptions?.disabledReasons?.map((r) => r.message) ?? []),
  ];
  const { productOfferingId, rpavId } = useRouteParams<ProductOfferingVersionParams>();
  const { triggerNotice } = useSnackbar();
  const navigate = useNavigate();
  const [saveProductOffering] = useReviewStep_SaveProductOfferingMutation();
  const { options, optionGroups, baseConfig } = productOffering;
  const formState = useFormState({
    config: formConfig,
    init: {
      input: { options, optionGroups },
      map: ({ options, optionGroups }) => ({
        options: options.filter((rpo) => rpo.active),
        optionGroups: optionGroups
          .filter((rpog) => rpog.active)
          .map((rpog) => ({
            id: rpog.id,
            checkoutOrder: rpog.checkoutOrder,
            overrideStep: rpog.overrideStep?.code,
            overrideSubstepNumber: rpog.overrideSubstepNumber,
          })),
      }),
    },
    readOnly,
  });

  return (
    <>
      <PageHeader
        title=""
        right={
          <>
            <Button
              size="lg"
              variant="quaternary"
              label={readOnly ? "Close" : "Cancel"}
              onClick={() => {
                if (devId) {
                  navigate(createDevelopmentProductOfferingUrl(devId, productOfferingId, rpavId));
                } else if (projectId) {
                  navigate(createProjectProductOfferingUrl(projectId, productOfferingId, rpavId));
                }
              }}
            />

            {!readOnly && (
              <Button
                size="lg"
                label="Save Options"
                onClick={async () => {
                  if (!formState) return;
                  const res = await saveProductOffering({
                    variables: {
                      input: {
                        id: productOffering.id,
                        options: formState.options.value,
                        optionGroups: formState.optionGroups.rows.map((group) => ({
                          id: group.id.value,
                          checkoutOrder: group.checkoutOrder.value ?? null,
                          overrideStep: group.overrideStep.value ?? null,
                          overrideSubstepNumber: group.overrideSubstepNumber.value ?? null,
                        })),
                      },
                    },
                  });
                  if (res.data?.saveProductOffering.productOffering) {
                    const productOfferingId = res.data?.saveProductOffering.productOffering.id;
                    triggerNotice({
                      message: "Offering successfully saved! Procurement will review and start bidding",
                    });
                    // TODO: route to a `global offering detail page` when offering is created from global library
                    const versionId = res.data?.saveProductOffering.productOffering.version.id;
                    if (devId) {
                      navigate(createDevelopmentProductOfferingUrl(devId, productOfferingId, versionId));
                    } else if (projectId) {
                      navigate(createProjectProductOfferingUrl(projectId, productOfferingId, versionId));
                    }
                  }
                }}
              />
            )}
          </>
        }
      />
      <OptionsView
        formState={formState}
        readyPlanOptions={options}
        baseConfig={baseConfig}
        readOnly={readOnly}
        disabledReasons={disabledReasons}
        checkoutConfigOptionGroupSteps={checkoutConfigOptionGroupSteps}
      />
    </>
  );
}
